import { PaymentsIcon } from 'core/components/Icons'
import { Badge, BadgeTone } from 'core/components/ui/Badge'
import { Column, Table } from 'core/components/ui/Table'
import { PAYMENT_MODE_LABELS, PaymentMode, PaymentRecord } from '../../domain/entities/Payment'

interface PaymentQueueProps {
  payments: PaymentRecord[]
  loading: boolean
  onReview: (record: PaymentRecord) => void
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage: number
}

const PAYMENT_MODE_TONE: Record<PaymentMode, BadgeTone> = {
  upi: 'primary',
  bank_transfer: 'info',
  cheque: 'secondary',
  cash: 'secondary',
  credit: 'warning',
}

const formatCurrency = (val: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(val)
}

const PAYMENT_STATUS_TONE: Record<string, BadgeTone> = {
  verified: 'success',
  rejected: 'danger',
  pending: 'warning',
  uploaded: 'warning',
}

export function PaymentQueue({
  payments,
  loading,
  onReview,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
}: PaymentQueueProps) {
  const columns: Column<PaymentRecord>[] = [
    {
      key: 'id',
      label: 'Order Reference',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-mono font-bold text-text-primary text-xs tracking-tight">{row.orderId}</span>
          <span className="text-[10px] text-text-secondary font-medium">Ref: {row.id}</span>
        </div>
      ),
    },
    {
      key: 'buyer',
      label: 'Buyer',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-semibold text-text-primary text-xs">
            {row.buyer?.businessName || row.buyer?.name || 'Unknown Buyer'}
          </span>
          <span className="text-[10px] text-text-secondary">{row.buyer?.phone || 'No phone'}</span>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount Paid',
      render: (row) => <span className="font-bold text-text-primary text-sm">{formatCurrency(row.amount)}</span>,
    },
    {
      key: 'mode',
      label: 'Payment Mode',
      render: (row) => (
        <Badge tone={PAYMENT_MODE_TONE[row.paymentMode]} variant="outline">
          {PAYMENT_MODE_LABELS[row.paymentMode]}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <Badge tone={PAYMENT_STATUS_TONE[row.status] || 'warning'} variant="subtle" className="capitalize">
          {row.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-center',
      render: (row) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => onReview(row)}
            className="p-1.5 rounded-btn hover:bg-bg text-text-secondary hover:text-text-primary transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)]"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            Review Payment
          </button>
        </div>
      ),
    },
  ]

  return (
    <Table
      columns={columns}
      data={payments}
      loading={loading}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
        totalItems,
        itemsPerPage,
      }}
      emptyMessage="No payment verifications in this queue."
      emptyIcon={<PaymentsIcon size={20} className="opacity-60" />}
    />
  )
}
